import { useState, useEffect, useCallback } from "react";
import { useFaust } from "../../contexts/FaustContext";
import { Button } from "../ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
    Play,
    Wrench,
    MoreHorizontal,
    Volume2,
    VolumeX,
    ChevronDown,
} from "lucide-react";
import { cn } from "../../lib/utils";

type DirectoryTree = {
    path: string;
    name: string;
    size: number;
    type: "directory" | "file";
    children?: DirectoryTree[];
    extension?: string;
};

export function EditorToolbar() {
    const { state, dispatch, runDsp, toggleOutput, editorRef, compileOptionsRef } = useFaust();
    const [examples, setExamples] = useState<DirectoryTree | null>(null);

    // Load examples
    useEffect(() => {
        fetch("./examples.json")
            .then((r) => r.json())
            .then((tree: DirectoryTree) => setExamples(tree))
            .catch(() => {});
    }, []);

    const handleRun = useCallback(async () => {
        const editor = editorRef.current;
        if (!editor) return;
        const code = editor.getValue();
        const result = await runDsp(code);
        if (result.success) {
            dispatch({ type: "SET_ACTIVE_BOTTOM_TAB", tab: "dsp" });
        }
    }, [runDsp, editorRef, dispatch]);

    const handleExampleClick = useCallback(
        async (path: string, name: string) => {
            try {
                const response = await fetch(path);
                const code = await response.text();
                if (editorRef.current) {
                    editorRef.current.setValue(code);
                }
            } catch {
                // failed to load example
            }
        },
        [editorRef]
    );

    const renderExampleTree = (tree: DirectoryTree) => {
        if (tree.type === "file") {
            return (
                <DropdownMenuItem
                    key={tree.path}
                    onSelect={() => handleExampleClick(tree.path, tree.name)}
                    className="text-xs cursor-pointer"
                >
                    {tree.name}
                </DropdownMenuItem>
            );
        }
        return (
            <DropdownMenuSub key={tree.name}>
                <DropdownMenuSubTrigger className="text-xs">
                    {tree.name}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto">
                    {tree.children?.map((child) => renderExampleTree(child))}
                </DropdownMenuSubContent>
            </DropdownMenuSub>
        );
    };

    return (
        <div className="h-9 flex items-center gap-1 px-1 bg-black border-b border-white/[0.08] shrink-0">
            {/* Left side buttons */}
            <div className="flex items-center gap-0.5">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-zinc-400 hover:text-zinc-100"
                            onClick={() => dispatch({ type: "TOGGLE_LEFT_PANEL" })}
                        >
                            <Wrench className="h-3.5 w-3.5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Toggle left panel</TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                                "h-7 w-7",
                                "text-[#40a6ff] hover:text-[#40a6ff] hover:bg-[#0078d4]/10"
                            )}
                            onClick={handleRun}
                            disabled={!state.isReady || state.isCompiling}
                        >
                            <Play className="h-3.5 w-3.5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Compile and run (Ctrl+R)</TooltipContent>
                </Tooltip>
            </div>

            {/* Examples dropdown */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-zinc-400 hover:text-zinc-100 gap-1 px-2"
                    >
                        Examples
                        <ChevronDown className="h-3 w-3" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    className="max-h-[400px] overflow-y-auto min-w-[180px]"
                >
                    {examples?.children?.map((child) => renderExampleTree(child))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Right side buttons */}
            <div className="flex items-center gap-0.5">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant={state.outputEnabled ? "default" : "ghost"}
                            size="icon"
                            className={cn(
                                "h-7 w-7",
                                state.outputEnabled
                                    ? "bg-[#0078d4] hover:bg-[#0078d4]/90 text-white"
                                    : "text-[#8b949e] hover:text-[#cccccc]"
                            )}
                            onClick={toggleOutput}
                        >
                            {state.outputEnabled ? (
                                <Volume2 className="h-3.5 w-3.5" />
                            ) : (
                                <VolumeX className="h-3.5 w-3.5" />
                            )}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        {state.outputEnabled ? "Output is On" : "Output is Off"}
                    </TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-zinc-400 hover:text-zinc-100"
                            onClick={() => dispatch({ type: "TOGGLE_RIGHT_PANEL" })}
                        >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Toggle right panel</TooltipContent>
                </Tooltip>
            </div>
        </div>
    );
}
